import React, { useState, useEffect, useRef } from 'react';
import {
  Plus,
  Trash2,
  Sparkles,
  Bot,
  Terminal,
  CheckCircle2,
  XCircle,
  FileText,
  ChevronRight,
  Loader2,
  Building,
  Shield,
  Users,
  BookOpen,
  ArrowLeft,
  X,
  RotateCcw,
  Search,
  ExternalLink
} from 'lucide-react';
import type { Perfil, Topico, Subtopico } from '../../types';
import { EcadSidebar } from '../../EcadSidebar';
import { EcadMetadataSection } from '../../EcadMetadataSection';
import { EcadPassosSection } from '../../EcadPassosSection';
import { EcadDryRunPanel } from '../../EcadDryRunPanel';
import { EcadMediaSection } from '../../EcadMediaSection';
import { EcadSimulationSection } from '../../EcadSimulationSection';
import { EcadCatalogDashboard } from '../../components/EcadCatalogDashboard';
import { EcadPerfilSummary } from '../../components/EcadPerfilSummary';
import { EcadTopicSummary } from '../../components/EcadTopicSummary';
import { EcadCopilotDrawer } from '../../components/EcadCopilotDrawer';
import { EcadZoomModal } from '../../EcadZoomModal';
import { EcadModals } from '../../EcadModals';
import { useEcadStudio } from '../../useEcadStudio';
import { useBatchProcessor } from './hooks/useBatchProcessor';

interface EcadStudioPanelProps {
  perfis: Perfil[];
  onRefreshCatalog: () => void;
  onBackToPlayer: () => void;
}

export function EcadStudioPanelModular({ perfis, onRefreshCatalog, onBackToPlayer }: EcadStudioPanelProps) {
  const [isCopilotOpen, setIsCopilotOpen] = useState(false);

  const mappedPerfis = React.useMemo(() => {
    // 1. Rename 'Equipe Interna' to 'Administrador'
    const updated = perfis.map(p => {
      let nome = p.nome;
      if (nome === 'Equipe Interna' || nome === 'Equipe interna') {
        nome = 'Administrador';
      }
      return { ...p, nome };
    });

    // 2. Sort by custom hierarchy order
    const getOrderIndex = (nome: string): number => {
      const n = nome.toLowerCase();
      if (n.includes('administrador') || n.includes('interna') || n.includes('interno')) return 0;
      if (n.includes('gestor') || n.includes('gerente')) return 1;
      if (n.includes('prefeito') || n.includes('prefeitura')) return 2;
      if (n.includes('diretor')) return 3;
      if (n.includes('coordenador')) return 4;
      if (n.includes('secretaria')) return 5;
      if (n.includes('responsavel') || n.includes('responsá')) return 6;
      if (n.includes('aluno')) return 7;
      return 8;
    };

    return [...updated].sort((a, b) => {
      const idxA = getOrderIndex(a.nome);
      const idxB = getOrderIndex(b.nome);
      if (idxA !== idxB) {
        return idxA - idxB;
      }
      return a.nome.localeCompare(b.nome);
    });
  }, [perfis]);

  const {
    selectedSubtopicoId,
    setSelectedSubtopicoId,
    selectedRoteiro,
    selectedPerfilId,
    setSelectedPerfilId,
    selectedTopicoId,
    setSelectedTopicoId,
    passos,
    loadingRoteiro,
    savingPassos,
    showPerfilModal,
    setShowPerfilModal,
    showTopicoModal,
    setShowTopicoModal,
    showSubtopicoModal,
    setShowSubtopicoModal,
    perfilForm,
    setPerfilForm,
    topicoForm,
    setTopicoForm,
    subtopicoForm,
    setSubtopicoForm,
    roteiroEditForm,
    setRoteiroEditForm,
    savingRoteiroMeta,
    isEditingMetadata,
    setIsEditingMetadata,
    zoomImage,
    setZoomImage,
    activeTab,
    setActiveTab,
    snapshots,
    loadingSnapshots,
    restoringSnapshot,
    passosEditMode,
    setPassosEditMode,
    jsonValue,
    setJsonValue,
    generatingPdf,
    pdfProgressShow,
    setPdfProgressShow,
    pdfProgressStep,
    pdfProgressError,
    pdfProgressUrl,
    pdfLogs,
    showScannerModal,
    setShowScannerModal,
    scannerForm,
    setScannerForm,
    scanningPage,
    isSimulating,
    simulationLogs,
    simulationSuccess,
    setSimulationSuccess,
    simulationModeVisible,
    setSimulationModeVisible,
    copilotMessages,
    copilotInput,
    setCopilotInput,
    copilotLoading,
    selectedStepIndexes,
    proposedUiActions,
    setProposedUiActions,
    activeEditingIndex,
    setActiveEditingIndex,
    includePerfilContext,
    setIncludePerfilContext,
    includeTopicoContext,
    setIncludeTopicoContext,
    includeSubtopicoContext,
    setIncludeSubtopicoContext,
    includePassosContext,
    setIncludePassosContext,
    handleCancelEditMeta,
    handleSaveRoteiroMeta,
    handleSavePassos,
    handleSaveFromJson,
    fetchSnapshots,
    handleRestoreVersion,
    handleGeneratePdf,
    handleRunSimulation,
    handleAddPasso,
    handleInsertPasso,
    handleRemovePasso,
    handleMovePasso,
    handleUpdatePassoField,
    handleToggleSelectStep,
    handleToggleSelectAllSteps,
    handleClearStepSelection,
    handleBulkDeleteSteps,
    handleExecuteUiActions,
    handleSendCopilotMessage,
    handleApplyAiSteps,
    handleApplyProposedHierarchy,
    handleCreatePerfil,
    handleCreateTopico,
    handleCreateSubtopico,
    handleDeleteSubtopico,
    handleDeleteTopico,
    handleScanPage,
    showArchivedModal,
    setShowArchivedModal,
    customTopicOrder,
    handleRenameEcadItem,
    handleArchiveEcadItem,
    handleRestoreEcadItem,
    handleApplyTopicOrder,

    // Novas propriedades do stream e controle do simulador
    activeStepOrdem,
    streamFrame,
    currentBrowserUrl,
    handleStopSimulation,
    handleRegeneratePassos,
    isPaused,
    handlePauseSimulation,
    handleResumeSimulation,
    handleStepForward,
    handleStepBackward,
    browserInstance,
    handleKillBrowser,
    handleRestartBrowser,
    handleJumpToStep,
    precacheInfo,
    toastMessage,
    toastType,
    showToast,
    uiMap,

    // Dry-Run
    isDryRunning,
    dryRunReport,
    handleDryRun,

    // Dry-Run Panel
    showDryRunPanel,
    setShowDryRunPanel,
    dryRunFullReport,
    retryingStep,
    openingStep,
    handleRetryStep,
    handleOpenPaused,
    handleEditFromDryRun,

    // Sessões do Copilot
    copilotSessionHistory,
    activeSessionId,
    handleNewCopilotSession,
  } = useEcadStudio(mappedPerfis, onRefreshCatalog);

  const selectedPerfil = mappedPerfis.find(p => p.id === selectedPerfilId);
  const [searchQuery, setSearchQuery] = useState('');

  const archivedTopicIds = React.useMemo(() => {
    if (!selectedPerfilId) return [];
    try {
      return JSON.parse(localStorage.getItem(`ecad_archived_topics_${selectedPerfilId}`) || '[]');
    } catch (e) {
      return [];
    }
  }, [selectedPerfilId, mappedPerfis]);

  const archivedTopics = React.useMemo(() => {
    if (!selectedPerfil) return [];
    return selectedPerfil.topicos.filter(t => archivedTopicIds.includes(t.id));
  }, [selectedPerfil, archivedTopicIds]);

  const archivedSubtopics = React.useMemo(() => {
    if (!selectedPerfil) return [];
    const list: Array<{ topico: Topico; sub: Subtopico }> = [];
    selectedPerfil.topicos.forEach(t => {
      (t.subtopicos || []).forEach(s => {
        if (s.status === 'ARQUIVADO') {
          list.push({ topico: t, sub: s });
        }
      });
    });
    return list;
  }, [selectedPerfil]);

  // Bottom Status Bar & Drawer States
  const [isBottomOpen, setIsBottomOpen] = useState(false);
  const [isBottomPinned, setIsBottomPinned] = useState(false);
  const [bottomActiveTab, setBottomActiveTab] = useState<'console' | 'simulator' | 'terminal' | 'preview'>('console');

  // Terminal CLI States
  const [terminalInput, setTerminalInput] = useState('');
  const [terminalHistory, setTerminalHistory] = useState<string[]>([
    'Bem-vindo ao Terminal de Orquestração do Aluno Conectado (v1.0.0).',
    'Digite "help" ou "?" para ver a lista de comandos disponíveis.',
    ''
  ]);

  // Hook processador em lote
  const {
    globalProcStatus,
    setGlobalProcStatus,
    globalProcProgress,
    setGlobalProcProgress,
    globalProcTimer,
    setGlobalProcTimer,
    globalProcLogs,
    setGlobalProcLogs,
    globalAutoShutdown,
    setGlobalAutoShutdown,
    queuedRoteiros,
    completedRoteiroIds,
    activeRoteiro,
    startBatchProcess,
    handleStartProcessPerfil,
    handleStartProcessTopico,
    handleStartProcessTudo,
  } = useBatchProcessor({
    mappedPerfis,
    selectedPerfil: selectedPerfil || null,
    showToast,
    setIsBottomOpen,
  });

  // Terminal CLI Command Execution Engine
  const executeTerminalCommand = (rawCmd: string) => {
    const cmd = rawCmd.trim().toLowerCase();
    if (!cmd) return;

    setTerminalHistory(prev => [...prev, `ac-orchestrator@aluno-conectado:~/automation$ ${rawCmd}`]);

    let response: string[] = [];
    if (cmd === 'help' || cmd === '?') {
      response = [
        'Comandos disponíveis:',
        '  help, ?          - Exibe esta lista de comandos.',
        '  status           - Exibe o status de saúde de todos os microsserviços.',
        '  docker ps        - Exibe os contêineres Docker ativos na máquina local.',
        '  logs             - Exibe os últimos 15 registros de automação.',
        '  redis-cli ping   - Testa a resposta e tempo do Redis Cache.',
        '  restart          - Reinicia a simulação e contêineres de suporte.',
        '  clear, cls       - Limpa a tela do terminal.'
      ];
    } else if (cmd === 'status') {
      response = [
        '[STATUS DE SAÚDE DOS SERVIÇOS]',
        '  ● PostgreSQL Database : ONLINE (Porta 5433)',
        '  ● Redis Cache Server  : ONLINE (Porta 6379)',
        '  ● NestJS Backend API  : ONLINE (Porta 3334)',
        `  ● Playwright Chrome   : \${isSimulating ? 'ACTIVE (Porta 3335)' : 'ONLINE (Porta 3335)'}`,
        '  ● Alice TTS Engine    : ONLINE'
      ];
    } else if (cmd === 'docker ps') {
      response = [
        'CONTAINER ID   IMAGE                 COMMAND                  CREATED        STATUS        PORTS',
        'a1b2c3d4e5f6   postgres:16-alpine    "docker-entrypoint.s…"   3 hours ago    Up 3 hours    0.0.0.0:5433->5432/tcp',
        'f6e5d4c3b2a1   redis:7-alpine        "docker-entrypoint.s…"   3 hours ago    Up 3 hours    0.0.0.0:6379->6379/tcp',
        'c3d4e5f6a1b2   aluno-nestjs-backend  "npm run start:dev"      2 hours ago    Up 2 hours    0.0.0.0:3334->3334/tcp',
        `e5f6a1b2c3d4   playwright-chrome     "node server.js"         1 hour ago     Up \${isSimulating ? 'Active' : '1 hour'}     0.0.0.0:3335->3335/tcp`
      ];
    } else if (cmd === 'logs') {
      const logsSource = isSimulating ? simulationLogs : globalProcLogs;
      if (logsSource.length === 0) {
        response = ['Sem registros de log disponíveis até o momento.'];
      } else {
        response = logsSource.slice(-15);
      }
    } else if (cmd === 'redis-cli ping') {
      response = [
        '127.0.0.1:6379> PING',
        'PONG (0.42ms)'
      ];
    } else if (cmd === 'restart') {
      response = [
        '[SYSTEM] Solicitando reinicialização dos contêineres...',
        '[DOCKER] Parando conteiner playwright-chrome... OK',
        '[DOCKER] Parando conteiner aluno-nestjs-backend... OK',
        '[DOCKER] Iniciando postgres:16-alpine... OK',
        '[DOCKER] Iniciando redis:7-alpine... OK',
        '[DOCKER] Iniciando aluno-nestjs-backend... OK',
        '[DOCKER] Iniciando playwright-chrome... OK',
        '[SYSTEM] Todos os serviços foram reiniciados com sucesso!'
      ];
    } else if (cmd === 'clear' || cmd === 'cls') {
      setTerminalHistory([]);
      setTerminalInput('');
      return;
    } else {
      response = [`ac-cli: comando não encontrado: "${rawCmd}". Digite "help" para ver os comandos.`];
    }

    setTerminalHistory(prev => [...prev, ...response, '']);
    setTerminalInput('');
  };

  // Scroll to bottom effect for terminal, console, and simulator logs
  useEffect(() => {
    if (bottomActiveTab === 'console') {
      const el = document.getElementById('bottom-console-anchor');
      el?.scrollIntoView({ behavior: 'smooth' });
    } else if (bottomActiveTab === 'simulator') {
      const el = document.getElementById('bottom-simulator-anchor');
      el?.scrollIntoView({ behavior: 'smooth' });
    } else if (bottomActiveTab === 'terminal') {
      const el = document.getElementById('bottom-terminal-anchor');
      el?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [globalProcLogs, simulationLogs, terminalHistory, bottomActiveTab, isBottomOpen]);

  // Automatically open bottom drawer and switch to simulator tab when simulation starts
  useEffect(() => {
    if (isSimulating) {
      setIsBottomOpen(true);
      setBottomActiveTab('simulator');
    }
  }, [isSimulating]);

  // Drawer auto-collapse timer (5 seconds) if not pinned
  useEffect(() => {
    if (globalProcStatus === 'stopped' && !isBottomPinned) {
      const timeout = setTimeout(() => {
        setIsBottomOpen(false);
      }, 5000);
      return () => clearTimeout(timeout);
    }
  }, [globalProcStatus, isBottomPinned]);

  // Derived computed states (batch vs single websocket simulation)
  const isCurrentlyRunning = globalProcStatus === 'running' || isSimulating;
  const activeStatus = isSimulating ? 'running' : globalProcStatus;
  const currentActiveRoteiro = isSimulating ? selectedRoteiro : activeRoteiro;
  const activeProgress = isSimulating
    ? (activeStepOrdem !== null ? Math.round((activeStepOrdem / (passos.length || 1)) * 100) : 0)
    : globalProcProgress;
  const activeTimer = isSimulating ? 0 : globalProcTimer;
  const activeLogs = isSimulating ? simulationLogs : globalProcLogs;
  const currentLatestLog = activeLogs.length > 0
    ? activeLogs[activeLogs.length - 1]
    : 'Pronto para iniciar processamento.';

  const handleGeneratePassosViaAi = () => {
    if (!selectedRoteiro) return;
    setIncludePerfilContext(false);
    setIncludeTopicoContext(false);
    setIncludeSubtopicoContext(true);

    const prompt = `Gere os passos de automação sugeridos para o roteiro "${selectedRoteiro.titulo}".`;
    handleSendCopilotMessage(prompt, {
      includePerfil: false,
      includeTopico: false,
      includeSubtopico: true,
      includePassos: false
    }, selectedRoteiro);
  };

  const pdfLogsEndRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (pdfProgressShow) {
      pdfLogsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [pdfLogs, pdfProgressShow]);

  const handleTriggerGlobalAction = (action: string, detail?: string) => {
    if (action === 'processa-all') {
      showToast('Iniciando processamento em lote de todos os perfis e roteiros...', 'success');
      handleStartProcessTudo();
    } else if (action === 'processa-update') {
      showToast('Verificando alterações e processando atualizações pendentes...', 'success');
      // Simulate check and process 2 subtopics
      const allSubs = mappedPerfis.flatMap(p => p.topicos.flatMap(t => t.subtopicos || []));
      startBatchProcess(allSubs.slice(0, 2));
    } else if (action === 'acompanha' || action === 'acompanhar-perfil' || action === 'acompanhar-topico') {
      showToast('Abrindo monitor em tempo real do processamento de simulação...', 'success');
      setIsBottomOpen(true);
    } else if (action === 'pendentes') {
      showToast('Exibindo fila de aprovações de novos roteiros de treinamento...', 'success');
    } else if (action === 'midia-final') {
      showToast('Visualizando mídia final (Vídeo e PDF gerado) do último lote...', 'success');
    } else if (action === 'processar-perfil') {
      showToast(`Iniciando processamento de roteiros para o perfil: ${detail}`, 'success');
      if (detail) handleStartProcessPerfil(detail);
    } else if (action === 'editar-perfil') {
      showToast(`Ação de edição para o perfil: ${detail}`, 'success');
    } else if (action === 'arquivar-perfil') {
      showToast(`Ação de arquivamento para o perfil: ${detail}`, 'success');
    } else if (action === 'processar-topico') {
      showToast(`Iniciando processamento de roteiros para o tópico: ${detail}`, 'success');
      if (detail) handleStartProcessTopico(detail);
    } else if (action === 'editar-topico') {
      showToast(`Ação de edição para o tópico: ${detail}`, 'success');
    } else if (action === 'arquivar-topico') {
      showToast(`Ação de arquivamento para o tópico: ${detail}`, 'success');
    }
  };



  useEffect(() => {
    setTimeout(() => setSearchQuery(''), 0);
  }, [selectedPerfilId, selectedTopicoId]);

  // Auto-scroll timeline to keep active step visible
  useEffect(() => {
    if (activeTab === 'simulacao' && activeStepOrdem !== null) {
      const timer = setTimeout(() => {
        const activeEl = document.getElementById(`timeline-step-${activeStepOrdem}`);
        if (activeEl) {
          activeEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [activeStepOrdem, activeTab]);

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      width: '100%',
      background: '#f8fafc',
      color: '#1e293b',
      fontFamily: "'Plus Jakarta Sans', sans-serif",
      overflow: 'hidden'
    }}>
      {/* Toast Notification */}
      {toastMessage && (
        <div style={{
          position: 'fixed',
          top: '16px',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 9999,
          background: toastType === 'success'
            ? 'linear-gradient(135deg, #065f46 0%, #047857 100%)'
            : 'linear-gradient(135deg, #7f1d1d 0%, #b91c1c 100%)',
          color: '#ffffff',
          padding: '12px 28px',
          borderRadius: '10px',
          fontSize: '0.85rem',
          fontWeight: 600,
          boxShadow: toastType === 'success'
            ? '0 4px 20px rgba(16, 185, 129, 0.35)'
            : '0 4px 20px rgba(239, 68, 68, 0.35)',
          border: `1px solid ${toastType === 'success' ? 'rgba(52, 211, 153, 0.3)' : 'rgba(248, 113, 113, 0.3)'}`,
          animation: 'toastSlideIn 0.3s ease-out',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          fontFamily: "'Plus Jakarta Sans', sans-serif",
          maxWidth: '500px'
        }}>
          <span style={{ fontSize: '16px' }}>{toastType === 'success' ? '✅' : '❌'}</span>
          {toastMessage}
        </div>
      )}
      <style>{`
        @keyframes toastSlideIn {
          from { opacity: 0; transform: translateX(-50%) translateY(-20px); }
          to { opacity: 1; transform: translateX(-50%) translateY(0); }
        }
      `}</style>
      {/* Main Workspace Layout (3 Columns) */}
      <div style={{
        display: 'flex',
        flex: 1,
        overflow: 'hidden',
        width: '100%'
      }}>

        {/* COLUNA 1: EXPLORER DE ROTEIROS OU LINHA DO TEMPO DA SIMULAÇÃO */}
        {activeTab === 'simulacao' ? (
          <div style={{
            width: '280px',
            background: '#ffffff',
            borderRight: '1px solid #e2e8f0',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            flexShrink: 0
          }}>
            <div style={{ padding: '20px', borderBottom: '1px solid #e2e8f0' }}>
              <h3 style={{ fontSize: '0.88rem', fontWeight: 800, color: '#0f172a', margin: 0, fontFamily: "'Outfit', sans-serif" }}>
                Linha do Tempo
              </h3>
              <p style={{ fontSize: '0.7rem', color: '#64748b', margin: '4px 0 0 0' }}>
                Progresso dos passos de simulação
              </p>
            </div>

            <div className="timeline-container" style={{ flex: 1, overflowY: 'auto', padding: '16px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {passos.map((p, idx) => {
                const isPassed = activeStepOrdem !== null && p.ordem < activeStepOrdem;
                const isActive = activeStepOrdem !== null && p.ordem === activeStepOrdem;
                const isFuture = activeStepOrdem !== null && p.ordem > activeStepOrdem;

                let circleBg = '#f1f5f9';
                let circleBorder = '1px solid #e2e8f0';
                let circleColor = '#64748b';
                let textColor = '#475569';

                if (isActive) {
                  circleBg = '#7c3aed';
                  circleBorder = '1px solid #c084fc';
                  circleColor = '#ffffff';
                  textColor = '#0f172a';
                } else if (isPassed) {
                  circleBg = 'rgba(124, 58, 237, 0.08)';
                  circleBorder = '1px solid rgba(124, 58, 237, 0.2)';
                  circleColor = '#7c3aed';
                  textColor = '#64748b';
                }

                return (
                  <div
                    key={p.ordem}
                    id={`timeline-step-${p.ordem}`}
                    onClick={() => {
                      if (isSimulating) {
                        if (isPaused) {
                          handleJumpToStep(p.ordem);
                        }
                      } else {
                        handleRunSimulation(p.ordem, true);
                      }
                    }}
                    style={{
                      display: 'flex',
                      gap: '12px',
                      alignItems: 'flex-start',
                      cursor: isSimulating ? (isPaused ? 'pointer' : 'not-allowed') : 'pointer',
                      opacity: isFuture ? 0.5 : 1,
                      transition: 'all 0.2s',
                      position: 'relative'
                    }}
                  >
                    {/* Linha vertical conectando os círculos */}
                    {idx < passos.length - 1 && (
                      <div style={{
                        position: 'absolute',
                        left: '13px',
                        top: '26px',
                        bottom: '-16px',
                        width: '2px',
                        background: isPassed ? 'rgba(124, 58, 237, 0.2)' : '#e2e8f0',
                        zIndex: 1
                      }} />
                    )}

                    <div style={{
                      width: '28px',
                      height: '28px',
                      borderRadius: '50%',
                      background: circleBg,
                      border: circleBorder,
                      color: circleColor,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.78rem',
                      fontWeight: 800,
                      flexShrink: 0,
                      zIndex: 2,
                      boxShadow: isActive ? '0 0 12px rgba(124, 58, 237, 0.4)' : 'none'
                    }}>
                      {p.ordem}
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', overflow: 'hidden' }}>
                      <span style={{
                        fontSize: '0.75rem',
                        fontWeight: isActive ? 700 : 600,
                        color: textColor
                      }}>
                        {p.tipoAcao}
                      </span>
                      <span style={{
                        fontSize: '0.68rem',
                        color: '#64748b',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: '200px'
                      }} title={p.legenda || ''}>
                        {p.legenda || 'Sem legenda'}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>

            <div style={{ padding: '16px', borderTop: '1px solid #e2e8f0' }}>
              <button
                onClick={() => setActiveTab('editor')}
                style={{
                  width: '100%',
                  background: '#f1f5f9',
                  border: '1px solid #e2e8f0',
                  borderRadius: '6px',
                  padding: '8px',
                  fontSize: '0.72rem',
                  fontWeight: 600,
                  color: '#475569',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                  transition: 'all 0.15s'
                }}
                onMouseEnter={e => e.currentTarget.style.background = '#e2e8f0'}
                onMouseLeave={e => e.currentTarget.style.background = '#f1f5f9'}
              >
                <ArrowLeft size={12} />
                Voltar ao Editor
              </button>
            </div>
          </div>
        ) : null}


        {/* COLUNA 2: CENTRAL EDITOR / TABS */}
        <main style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          background: '#f8fafc',
          borderRight: selectedPerfilId !== null ? '1px solid #e2e8f0' : 'none',
          position: 'relative'
        }}>
          {!selectedSubtopicoId ? (
            <div style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              overflowY: 'auto',
              padding: '32px 40px',
              background: '#f8fafc'
            }}>
              {selectedPerfilId && !selectedTopicoId ? (
                <EcadPerfilSummary
                  selectedPerfil={selectedPerfil}
                  selectedPerfilId={selectedPerfilId}
                  searchQuery={searchQuery}
                  setSearchQuery={setSearchQuery}
                  setSelectedTopicoId={setSelectedTopicoId}
                  setSelectedSubtopicoId={setSelectedSubtopicoId}
                  setTopicoForm={setTopicoForm}
                  setShowTopicoModal={setShowTopicoModal}
                  onTriggerAction={handleTriggerGlobalAction}
                  globalProcStatus={globalProcStatus}
                  setGlobalProcStatus={setGlobalProcStatus}
                  globalProcProgress={globalProcProgress}
                  setGlobalProcProgress={setGlobalProcProgress}
                  globalProcTimer={globalProcTimer}
                  setGlobalProcTimer={setGlobalProcTimer}
                  globalProcLogs={globalProcLogs}
                  setGlobalProcLogs={setGlobalProcLogs}
                  globalAutoShutdown={globalAutoShutdown}
                  setGlobalAutoShutdown={setGlobalAutoShutdown}
                  completedRoteiroIds={completedRoteiroIds}
                  queuedRoteiros={queuedRoteiros}
                  activeRoteiro={activeRoteiro}
                />
              ) : selectedPerfilId && selectedTopicoId ? (
                <EcadTopicSummary
                  selectedPerfil={selectedPerfil}
                  selectedPerfilId={selectedPerfilId}
                  selectedTopicoId={selectedTopicoId}
                  setSelectedPerfilId={setSelectedPerfilId}
                  setSelectedTopicoId={setSelectedTopicoId}
                  setSelectedSubtopicoId={setSelectedSubtopicoId}
                  searchQuery={searchQuery}
                  setSearchQuery={setSearchQuery}
                  setSubtopicoForm={setSubtopicoForm}
                  setShowSubtopicoModal={setShowSubtopicoModal}
                  onTriggerAction={handleTriggerGlobalAction}
                  globalProcStatus={globalProcStatus}
                  setGlobalProcStatus={setGlobalProcStatus}
                  globalProcProgress={globalProcProgress}
                  setGlobalProcProgress={setGlobalProcProgress}
                  globalProcTimer={globalProcTimer}
                  setGlobalProcTimer={setGlobalProcTimer}
                  globalProcLogs={globalProcLogs}
                  setGlobalProcLogs={setGlobalProcLogs}
                  globalAutoShutdown={globalAutoShutdown}
                  setGlobalAutoShutdown={setGlobalAutoShutdown}
                  completedRoteiroIds={completedRoteiroIds}
                  queuedRoteiros={queuedRoteiros}
                  activeRoteiro={activeRoteiro}
                />
              ) : (
                <EcadCatalogDashboard
                  perfis={mappedPerfis}
                  searchQuery={searchQuery}
                  setSearchQuery={setSearchQuery}
                  setSelectedPerfilId={setSelectedPerfilId}
                  setShowPerfilModal={setShowPerfilModal}
                  onTriggerAction={handleTriggerGlobalAction}
                  globalProcStatus={globalProcStatus}
                  setGlobalProcStatus={setGlobalProcStatus}
                  globalProcProgress={globalProcProgress}
                  setGlobalProcProgress={setGlobalProcProgress}
                  globalProcTimer={globalProcTimer}
                  setGlobalProcTimer={setGlobalProcTimer}
                  globalProcLogs={globalProcLogs}
                  setGlobalProcLogs={setGlobalProcLogs}
                  globalAutoShutdown={globalAutoShutdown}
                  setGlobalAutoShutdown={setGlobalAutoShutdown}
                  completedRoteiroIds={completedRoteiroIds}
                  queuedRoteiros={queuedRoteiros}
                  activeRoteiro={activeRoteiro}
                />
              )}
            </div>
          ) : loadingRoteiro ? (
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div style={{ textAlign: 'center' }}>
                <Loader2 size={32} style={{ color: '#8b5cf6', animation: 'spin 1s linear infinite', margin: '0 auto 12px auto' }} />
                <p style={{ fontSize: '0.85rem', color: '#94a3b8' }}>Carregando dados do roteiro...</p>
              </div>
            </div>
          ) : (
            <>
              {/* Tab navigation */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderBottom: '1px solid #e2e8f0',
                background: '#ffffff',
                padding: '0 24px',
                height: '56px',
                flexShrink: 0
              }}>
                <div style={{ display: 'flex', gap: '4px', height: '100%' }}>
                  <button
                    onClick={() => setActiveTab('editor')}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '0 16px',
                      border: 'none',
                      background: 'none',
                      borderBottom: activeTab === 'editor' ? '2px solid #7c3aed' : '2px solid transparent',
                      color: activeTab === 'editor' ? '#7c3aed' : '#64748b',
                      fontWeight: activeTab === 'editor' ? 700 : 500,
                      fontSize: '0.82rem',
                      cursor: 'pointer',
                      height: '100%',
                      transition: 'all 0.2s'
                    }}
                  >
                    <Sparkles size={14} />
                    Roteiro
                  </button>
                  <button
                    onClick={() => setActiveTab('midias')}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '0 16px',
                      border: 'none',
                      background: 'none',
                      borderBottom: activeTab === 'midias' ? '2px solid #7c3aed' : '2px solid transparent',
                      color: activeTab === 'midias' ? '#7c3aed' : '#64748b',
                      fontWeight: activeTab === 'midias' ? 700 : 500,
                      fontSize: '0.82rem',
                      cursor: 'pointer',
                      height: '100%',
                      transition: 'all 0.2s'
                    }}
                  >
                    <FileText size={14} />
                    Arquivos
                  </button>
                  <button
                    onClick={() => setActiveTab('simulacao')}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '0 16px',
                      border: 'none',
                      background: 'none',
                      borderBottom: activeTab === 'simulacao' ? '2px solid #7c3aed' : '2px solid transparent',
                      color: activeTab === 'simulacao' ? '#7c3aed' : '#64748b',
                      fontWeight: activeTab === 'simulacao' ? 700 : 500,
                      fontSize: '0.82rem',
                      cursor: 'pointer',
                      height: '100%',
                      transition: 'all 0.2s'
                    }}
                  >
                    <Terminal size={14} />
                    Simulador
                  </button>
                  <button
                    onClick={() => setActiveTab('restaurar')}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '0 16px',
                      border: 'none',
                      background: 'none',
                      borderBottom: activeTab === 'restaurar' ? '2px solid #7c3aed' : '2px solid transparent',
                      color: activeTab === 'restaurar' ? '#7c3aed' : '#64748b',
                      fontWeight: activeTab === 'restaurar' ? 700 : 500,
                      fontSize: '0.82rem',
                      cursor: 'pointer',
                      height: '100%',
                      transition: 'all 0.2s'
                    }}
                  >
                    <RotateCcw size={14} />
                    Restaurar
                  </button>
                </div>

                <div style={{ fontSize: '0.78rem', color: '#64748b', fontWeight: 600 }}>
                  Roteiro: <span style={{ color: '#7c3aed' }}>{selectedRoteiro?.titulo}</span>
                </div>
              </div>

              {/* Scrollable content */}
              <div style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                overflowY: activeTab === 'simulacao' ? 'hidden' : 'auto',
                padding: activeTab === 'simulacao' ? '0' : '24px',
                gap: activeTab === 'simulacao' ? '0' : '24px'
              }}>
                {activeTab === 'editor' && (
                  <>
                    <EcadMetadataSection
                      selectedRoteiro={selectedRoteiro}
                      roteiroEditForm={roteiroEditForm}
                      setRoteiroEditForm={setRoteiroEditForm}
                      isEditingMetadata={isEditingMetadata}
                      setIsEditingMetadata={setIsEditingMetadata}
                      savingRoteiroMeta={savingRoteiroMeta}
                      handleSaveRoteiroMeta={handleSaveRoteiroMeta}
                      handleCancelEditMeta={handleCancelEditMeta}
                    />

                    <EcadPassosSection
                      selectedSubtopicoId={selectedSubtopicoId}
                      passos={passos}
                      passosEditMode={passosEditMode}
                      setPassosEditMode={setPassosEditMode}
                      jsonValue={jsonValue}
                      setJsonValue={setJsonValue}
                      savingPassos={savingPassos}
                      handleAddPasso={handleAddPasso}
                      handleInsertPasso={handleInsertPasso}
                      handleRemovePasso={handleRemovePasso}
                      handleMovePasso={handleMovePasso}
                      handleUpdatePassoField={handleUpdatePassoField}
                      handleSavePassos={handleSavePassos}
                      handleSaveFromJson={handleSaveFromJson}
                      setShowScannerModal={setShowScannerModal}
                      setZoomImage={setZoomImage}
                      onRunFromStep={(ordem) => {
                        setActiveTab('simulacao');
                        handleRunSimulation(ordem);
                      }}
                      selectedStepIndexes={selectedStepIndexes}
                      onToggleSelectStep={handleToggleSelectStep}
                      onToggleSelectAllSteps={handleToggleSelectAllSteps}
                      onActiveEditingIndex={setActiveEditingIndex}
                      selectedRoteiro={selectedRoteiro}
                      activeVersion={selectedRoteiro?.activeVersion}
                      onRunSimulation={() => {
                        setActiveTab('simulacao');
                        handleRunSimulation();
                      }}
                      isSimulating={isSimulating}
                      handleRegeneratePassos={handleRegeneratePassos}
                      onGeneratePassosViaAi={handleGeneratePassosViaAi}
                      uiMap={uiMap}
                      onDryRun={() => {
                        handleDryRun();
                      }}
                      isDryRunning={isDryRunning}
                    />

                    {/* Dry-Run Debug Panel (aparece inline após testar) */}
                    {showDryRunPanel && dryRunFullReport && (
                      <EcadDryRunPanel
                        report={dryRunFullReport.report}
                        durationMs={dryRunFullReport.durationMs}
                        aprovados={dryRunFullReport.aprovados}
                        falhas={dryRunFullReport.falhas}
                        pulados={dryRunFullReport.pulados}
                        onClose={() => setShowDryRunPanel(false)}
                        onRetryStep={handleRetryStep}
                        onOpenPaused={handleOpenPaused}
                        onEditStep={handleEditFromDryRun}
                        retryingStep={retryingStep}
                        openingStep={openingStep}
                        passos={passos}
                      />
                    )}
                  </>
                )}

                {activeTab === 'midias' && (
                  <EcadMediaSection
                    passos={passos}
                    roteiroEditForm={roteiroEditForm}
                    generatingPdf={generatingPdf}
                    handleGeneratePdf={handleGeneratePdf}
                  />
                )}

                {activeTab === 'simulacao' && (
                  <EcadSimulationSection
                    passos={passos}
                    isSimulating={isSimulating}
                    isPaused={isPaused}
                    simulationLogs={simulationLogs}
                    simulationSuccess={simulationSuccess}
                    simulationModeVisible={simulationModeVisible}
                    setSimulationModeVisible={setSimulationModeVisible}
                    handleRunSimulation={handleRunSimulation}
                    streamFrame={streamFrame}
                    currentBrowserUrl={currentBrowserUrl}
                    handleStopSimulation={handleStopSimulation}
                    activeStepOrdem={activeStepOrdem}
                    handlePauseSimulation={handlePauseSimulation}
                    handleResumeSimulation={handleResumeSimulation}
                    handleStepForward={handleStepForward}
                    handleStepBackward={handleStepBackward}
                    browserInstance={browserInstance}
                    handleKillBrowser={handleKillBrowser}
                    handleRestartBrowser={handleRestartBrowser}
                    pdfUrl={roteiroEditForm.pdfUrl}
                    videoUrl={roteiroEditForm.videoUrl}
                    generatingPdf={generatingPdf}
                    pdfProgressError={pdfProgressError}
                    handleGeneratePdf={handleGeneratePdf}
                    setSimulationSuccess={setSimulationSuccess}
                    precacheInfo={precacheInfo}
                  />
                )}

                {activeTab === 'restaurar' && (
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '20px',
                    animation: 'fadeIn 0.3s ease-in-out'
                  }}>
                    <style dangerouslySetInnerHTML={{
                      __html: `
                      @keyframes fadeIn {
                        from { opacity: 0; transform: translateY(10px); }
                        to { opacity: 1; transform: translateY(0); }
                      }
                    `}} />
                    <div style={{
                      background: 'rgba(15, 23, 42, 0.6)',
                      border: '1px solid rgba(255, 255, 255, 0.08)',
                      borderRadius: '12px',
                      padding: '20px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: '16px'
                    }}>
                      <div>
                        <h3 style={{ fontSize: '1rem', fontWeight: 800, color: '#f8fafc', marginBottom: '4px' }}>
                          Histórico de Snapshots & Pontos de Restauração
                        </h3>
                        <p style={{ fontSize: '0.8rem', color: '#94a3b8', margin: 0, lineHeight: 1.4 }}>
                          Cada modificação no roteiro ou nos passos gera automaticamente um ponto de restauração.
                          Você pode retornar a qualquer ponto anterior. Arquivos de mídia e áudios já gerados no disco não são deletados, apenas reassociados conforme a versão correspondente.
                        </p>
                      </div>
                      <button
                        onClick={() => selectedSubtopicoId && fetchSnapshots(selectedSubtopicoId)}
                        disabled={loadingSnapshots}
                        style={{
                          background: 'rgba(139, 92, 246, 0.1)',
                          border: '1px solid rgba(139, 92, 246, 0.3)',
                          borderRadius: '8px',
                          color: '#c084fc',
                          padding: '8px 16px',
                          fontSize: '0.78rem',
                          fontWeight: 700,
                          cursor: 'pointer',
                          whiteSpace: 'nowrap',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          transition: 'all 0.2s'
                        }}
                      >
                        {loadingSnapshots ? (
                          <Loader2 size={12} className="spin" />
                        ) : (
                          <RotateCcw size={12} />
                        )}
                        Atualizar Lista
                      </button>
                    </div>

                    {loadingSnapshots ? (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '200px' }}>
                        <div style={{ textAlign: 'center' }}>
                          <Loader2 size={24} style={{ color: '#8b5cf6', animation: 'spin 1s linear infinite', marginBottom: '8px' }} />
                          <p style={{ fontSize: '0.8rem', color: '#64748b' }}>Carregando histórico de backups...</p>
                        </div>
                      </div>
                    ) : snapshots.length === 0 ? (
                      <div style={{
                        border: '1px dashed rgba(255, 255, 255, 0.1)',
                        borderRadius: '12px',
                        padding: '40px',
                        textAlign: 'center',
                        color: '#64748b',
                        background: 'rgba(15, 23, 42, 0.2)'
                      }}>
                        <RotateCcw size={32} style={{ color: 'rgba(139, 92, 246, 0.2)', marginBottom: '12px' }} />
                        <p style={{ fontSize: '0.85rem', color: '#94a3b8', fontWeight: 600, marginBottom: '4px' }}>
                          Nenhum Ponto de Restauração Encontrado
                        </p>
                        <p style={{ fontSize: '0.78rem', maxWidth: '300px', margin: '0 auto', lineHeight: 1.4 }}>
                          Pontos de backup serão criados automaticamente assim que você realizar qualquer alteração nos passos ou dados deste roteiro.
                        </p>
                      </div>
                    ) : (
                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '12px',
                        position: 'relative',
                        paddingLeft: '24px',
                        borderLeft: '2px solid rgba(139, 92, 246, 0.15)'
                      }}>
                        {snapshots.map((snap, idx) => {
                          let formattedDate = snap.timestamp;
                          try {
                            const datePart = snap.timestamp.split('T')[0];
                            const timePart = snap.timestamp.split('T')[1]?.split('-')[0]?.replace(/-/g, ':') || '';
                            const [year, month, day] = datePart.split('-');
                            formattedDate = `${day}/${month}/${year} ${timePart.split('.')[0]}`;
                          } catch {
                            // ignore
                          }

                          return (
                            <div
                              key={snap.timestamp}
                              style={{
                                position: 'relative',
                                background: 'rgba(15, 23, 42, 0.4)',
                                border: '1px solid rgba(255, 255, 255, 0.05)',
                                borderRadius: '12px',
                                padding: '16px 20px',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                gap: '20px',
                                transition: 'all 0.2s'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = 'rgba(15, 23, 42, 0.6)';
                                e.currentTarget.style.borderColor = 'rgba(139, 92, 246, 0.2)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'rgba(15, 23, 42, 0.4)';
                                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.05)';
                              }}
                            >
                              <div style={{
                                position: 'absolute',
                                left: '-31px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                width: '12px',
                                height: '12px',
                                borderRadius: '50%',
                                background: idx === 0 ? '#a78bfa' : '#334155',
                                border: '3px solid #090d16',
                                boxShadow: idx === 0 ? '0 0 10px #8b5cf6' : 'none',
                                zIndex: 10
                              }} />

                              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <span style={{ fontSize: '0.85rem', fontWeight: 800, color: '#f8fafc' }}>
                                    {snap.version ? `${snap.version} - ` : ''}{snap.actionDescription}
                                  </span>
                                  {idx === 0 && (
                                    <span style={{
                                      fontSize: '0.62rem',
                                      fontWeight: 800,
                                      color: '#a78bfa',
                                      background: 'rgba(139, 92, 246, 0.15)',
                                      border: '1px solid rgba(139, 92, 246, 0.25)',
                                      padding: '2px 6px',
                                      borderRadius: '4px'
                                    }}>
                                      Última Modificação
                                    </span>
                                  )}
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: '0.75rem', color: '#64748b' }}>
                                  <span>{formattedDate}</span>
                                  <span>•</span>
                                  <span style={{ color: '#94a3b8', fontWeight: 600 }}>{snap.stepCount} passo(s)</span>
                                </div>
                              </div>

                              <button
                                onClick={() => handleRestoreVersion(snap.timestamp)}
                                disabled={restoringSnapshot}
                                style={{
                                  background: idx === 0 ? 'rgba(255, 255, 255, 0.03)' : '#8b5cf6',
                                  border: idx === 0 ? '1px solid rgba(255, 255, 255, 0.08)' : 'none',
                                  borderRadius: '8px',
                                  color: idx === 0 ? '#94a3b8' : '#ffffff',
                                  padding: '8px 16px',
                                  fontSize: '0.78rem',
                                  fontWeight: 700,
                                  cursor: 'pointer',
                                  transition: 'all 0.2s',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '6px'
                                }}
                                onMouseEnter={(e) => {
                                  if (idx === 0) {
                                    e.currentTarget.style.background = 'rgba(255,255,255,0.06)';
                                    e.currentTarget.style.color = '#ffffff';
                                  } else {
                                    e.currentTarget.style.background = '#7c3aed';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  if (idx === 0) {
                                    e.currentTarget.style.background = 'rgba(255,255,255,0.03)';
                                    e.currentTarget.style.color = '#94a3b8';
                                  } else {
                                    e.currentTarget.style.background = '#8b5cf6';
                                  }
                                }}
                              >
                                <RotateCcw size={12} />
                                Restaurar
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* FLOATING SELECTION BOTTOM BAR */}
              {selectedStepIndexes.length > 0 && (
                <div style={{
                  position: 'absolute',
                  bottom: '24px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  background: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)',
                  backdropFilter: 'blur(12px)',
                  border: '1px solid rgba(255, 255, 255, 0.25)',
                  borderRadius: '16px',
                  padding: '12px 20px',
                  display: 'flex',
                  alignItems: 'center',
                  flexDirection: 'row',
                  flexWrap: 'nowrap',
                  gap: '16px',
                  boxShadow: '0 10px 30px rgba(139, 92, 246, 0.3), 0 0 15px rgba(139, 92, 246, 0.2)',
                  zIndex: 50,
                  width: 'max-content',
                  maxWidth: '95%',
                  transition: 'all 0.3s ease-in-out',
                  animation: 'slideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1)'
                }}>
                  <style dangerouslySetInnerHTML={{
                    __html: `
                    @keyframes slideUp {
                      from { transform: translate(-50%, 20px); opacity: 0; }
                      to { transform: translate(-50%, 0); opacity: 1; }
                    }
                  `}} />

                  {/* Selected step numbers list (badges) */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    flexShrink: 1,
                    minWidth: 0,
                    overflowX: 'auto',
                    maxWidth: '350px',
                    paddingRight: '4px'
                  }}>
                    {selectedStepIndexes.sort((a, b) => a - b).map(idx => (
                      <div
                        key={idx}
                        style={{
                          width: '32px',
                          height: '32px',
                          borderRadius: '8px',
                          background: 'rgba(13, 10, 32, 0.4)',
                          border: '1px solid rgba(139, 92, 246, 0.35)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '0.78rem',
                          fontWeight: 700,
                          color: '#a78bfa',
                          flexShrink: 0
                        }}
                        title={`Passo #${idx + 1}`}
                      >
                        #{idx + 1}
                      </div>
                    ))}
                  </div>

                  {/* Divider */}
                  <div style={{ width: '1px', height: '20px', background: 'rgba(255, 255, 255, 0.25)', flexShrink: 0 }} />

                  {/* Actions */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0, flexWrap: 'nowrap' }}>
                    <button
                      onClick={() => {
                        const stepsRef = selectedStepIndexes.map(idx => `#${idx + 1}`).join(', ');
                        setCopilotInput(`Sobre os passos ${stepsRef}: `);
                        setTimeout(() => {
                          const inputEl = document.querySelector('input[placeholder="Pergunte à IA do Estúdio..."]') as HTMLInputElement;
                          inputEl?.focus();
                        }, 100);
                      }}
                      style={{
                        background: 'rgba(0, 0, 0, 0.15)',
                        border: '1px solid rgba(255, 255, 255, 0.25)',
                        borderRadius: '8px',
                        padding: '6px 12px',
                        color: '#ffffff',
                        fontSize: '0.72rem',
                        fontWeight: 700,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        transition: 'all 0.2s',
                        whiteSpace: 'nowrap',
                        flexShrink: 0
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(0, 0, 0, 0.25)'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(0, 0, 0, 0.15)'}
                    >
                      <Bot size={12} style={{ flexShrink: 0 }} /> Falar com IA
                    </button>

                    <button
                      onClick={handleBulkDeleteSteps}
                      style={{
                        background: 'rgba(220, 38, 38, 0.95)',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        borderRadius: '8px',
                        padding: '6px 10px',
                        color: '#ffffff',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.2s',
                        flexShrink: 0
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(185, 28, 28, 1)'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(220, 38, 38, 0.95)'}
                      title="Excluir Selecionados"
                    >
                      <Trash2 size={13} style={{ flexShrink: 0 }} />
                    </button>

                    <button
                      onClick={handleClearStepSelection}
                      style={{
                        background: 'rgba(255, 255, 255, 0.15)',
                        border: '1px solid rgba(255, 255, 255, 0.25)',
                        borderRadius: '8px',
                        padding: '6px 10px',
                        color: '#ffffff',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.2s',
                        flexShrink: 0
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.25)'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)'}
                      title="Cancelar Seleção"
                    >
                      <X size={13} style={{ flexShrink: 0 }} />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </main>

        {/* COLUNA 3: AI COPILOT CHAT PANEL (GAVETA RETRÁTIL) */}
        <EcadCopilotDrawer
          isOpen={isCopilotOpen}
          onClose={() => setIsCopilotOpen(false)}
          copilotMessages={copilotMessages}
          copilotInput={copilotInput}
          setCopilotInput={setCopilotInput}
          copilotLoading={copilotLoading}
          handleSendCopilotMessage={handleSendCopilotMessage}
          handleApplyAiSteps={handleApplyAiSteps}
          handleApplyProposedHierarchy={handleApplyProposedHierarchy}
          proposedUiActions={proposedUiActions}
          onConfirmUiActions={() => handleExecuteUiActions(proposedUiActions || [])}
          onRejectUiActions={() => setProposedUiActions(null)}
          activeEditingIndex={activeEditingIndex}
          selectedStepIndexes={selectedStepIndexes}
          includePerfilContext={includePerfilContext}
          setIncludePerfilContext={setIncludePerfilContext}
          includeTopicoContext={includeTopicoContext}
          setIncludeTopicoContext={setIncludeTopicoContext}
          includeSubtopicoContext={includeSubtopicoContext}
          setIncludeSubtopicoContext={setIncludeSubtopicoContext}
          includePassosContext={includePassosContext}
          setIncludePassosContext={setIncludePassosContext}
          selectedRoteiro={selectedRoteiro}
          passos={passos}
          activeStepOrdem={activeStepOrdem}
          activeTab={activeTab}
          simulationLogs={simulationLogs}
          perfis={perfis}
          selectedPerfilId={selectedPerfilId}
          selectedTopicoId={selectedTopicoId}
          handleApplyTopicOrder={handleApplyTopicOrder}
          handleRenameEcadItem={handleRenameEcadItem}
          handleArchiveEcadItem={handleArchiveEcadItem}
          onNewSession={handleNewCopilotSession}
          sessionHistory={copilotSessionHistory}
        />

        {/* Botão Flutuante da Alice (Copiloto AI) */}
        {selectedPerfilId !== null && !isCopilotOpen && (
          <button
            onClick={() => setIsCopilotOpen(true)}
            style={{
              position: 'fixed',
              bottom: '24px',
              right: '24px',
              width: '48px',
              height: '48px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)',
              color: '#ffffff',
              border: 'none',
              boxShadow: '0 4px 16px rgba(124, 58, 237, 0.35)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 9997,
              transition: 'all 0.2s ease-in-out',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'scale(1.08)';
              e.currentTarget.style.boxShadow = '0 6px 20px rgba(124, 58, 237, 0.45)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.boxShadow = '0 4px 16px rgba(124, 58, 237, 0.35)';
            }}
            title="Abrir Copiloto Alice"
          >
            <Bot size={22} />
          </button>
        )}

      </div>

      {/* GLOBAL BOTTOM STATUS BAR & DEV DRAWER */}
      <div style={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        zIndex: 9998,
        position: 'relative',
        flexShrink: 0
      }}>
        {/* Status Bar */}
        <div
          onClick={() => setIsBottomOpen(!isBottomOpen)}
          style={{
            height: '32px',
            background: '#0f172a',
            borderTop: '1px solid #1e293b',
            color: '#94a3b8',
            padding: '0 16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            fontSize: '0.72rem',
            cursor: 'pointer',
            userSelect: 'none',
            transition: 'background 0.2s',
            fontFamily: "'Inter', sans-serif"
          }}
          onMouseEnter={(e) => e.currentTarget.style.background = '#1e293b'}
          onMouseLeave={(e) => e.currentTarget.style.background = '#0f172a'}
        >
          {/* Left: LED indicator & Latest log */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1, minWidth: 0 }}>
            {/* LED Dot */}
            <div
              className={activeStatus === 'running' ? 'pulse-led-green' : ''}
              style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                background: activeStatus === 'running'
                  ? '#10b981'
                  : activeStatus === 'stopping'
                    ? '#ea580c'
                    : activeStatus === 'stopped'
                      ? '#ef4444'
                      : '#3b82f6',
                boxShadow: activeStatus === 'running' ? '0 0 8px #10b981' : 'none'
              }}
            />

            <span style={{ fontWeight: 700, color: '#f8fafc', whiteSpace: 'nowrap' }}>
              {activeStatus === 'running'
                ? '● MOTOR ATIVO'
                : activeStatus === 'stopped'
                  ? '● MOTOR DETIDO'
                  : '● SISTEMA PRONTO'}
            </span>

            {currentActiveRoteiro && (
              <span style={{ color: '#a78bfa', fontWeight: 600, background: 'rgba(167, 139, 250, 0.1)', padding: '2px 6px', borderRadius: '4px', whiteSpace: 'nowrap' }}>
                {currentActiveRoteiro.titulo}
              </span>
            )}

            {/* Separator */}
            <span style={{ color: '#334155' }}>|</span>

            {/* Latest Log Line */}
            <span style={{
              color: '#cbd5e1',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontFamily: 'monospace',
              fontSize: '0.7rem'
            }}>
              {currentLatestLog}
            </span>
          </div>

          {/* Right: Progress bar & controls */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
            {/* Checkbox Fixar Console */}
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '0.68rem', color: '#94a3b8' }}>
              <input
                type="checkbox"
                checked={isBottomPinned}
                onChange={(e) => setIsBottomPinned(e.target.checked)}
                style={{ cursor: 'pointer' }}
              />
              Fixar Console
            </label>

            {/* Mini Progress */}
            {activeStatus === 'running' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '0.68rem', fontWeight: 700, color: '#10b981' }}>{activeProgress}%</span>
                <div style={{ width: '60px', height: '4px', background: '#334155', borderRadius: '2px', overflow: 'hidden' }}>
                  <div style={{ width: `${activeProgress}%`, height: '100%', background: '#10b981' }} />
                </div>
              </div>
            )}

            {activeStatus === 'running' && activeTimer > 0 && (
              <span style={{ fontWeight: 600, color: '#f8fafc', fontSize: '0.68rem', fontFamily: 'monospace' }}>
                {Math.floor(activeTimer / 60).toString().padStart(2, '0')}:{(activeTimer % 60).toString().padStart(2, '0')}
              </span>
            )}

            {/* Expand indicator */}
            <div
              onClick={() => setIsBottomOpen(!isBottomOpen)}
              style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', padding: '2px' }}
            >
              <span style={{ fontSize: '10px' }}>{isBottomOpen ? '▼' : '▲'}</span>
            </div>
          </div>
        </div>

        {/* Console Drawer (Floating Drawer) */}
        <div style={{
          height: isBottomOpen ? '280px' : '0',
          background: '#090d16',
          borderTop: isBottomOpen ? '1px solid #7c3aed' : 'none',
          boxShadow: isBottomOpen ? '0 -10px 25px rgba(0,0,0,0.5)' : 'none',
          transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          width: '100%',
          fontFamily: "'Inter', sans-serif"
        }}>
          {/* Header/Tabs */}
          <div style={{
            height: '36px',
            background: '#0b0f19',
            borderBottom: '1px solid #1e293b',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '0 16px',
            flexShrink: 0
          }}>
            <div style={{ display: 'flex', gap: '4px', height: '100%' }}>
              {[
                { id: 'console', label: 'Console de Automação' },
                { id: 'simulator', label: 'Simulador Playwright' },
                { id: 'terminal', label: 'Terminal Interativo (CLI)' },
                { id: 'preview', label: 'Live Browser Preview' }
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setBottomActiveTab(tab.id as any)}
                  style={{
                    padding: '0 12px',
                    border: 'none',
                    background: 'none',
                    color: bottomActiveTab === tab.id ? '#a78bfa' : '#64748b',
                    fontSize: '0.72rem',
                    fontWeight: bottomActiveTab === tab.id ? 700 : 500,
                    cursor: 'pointer',
                    height: '100%',
                    borderBottom: bottomActiveTab === tab.id ? '2px solid #7c3aed' : '2px solid transparent',
                    transition: 'all 0.15s'
                  }}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <button
                onClick={() => setIsBottomOpen(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#64748b',
                  cursor: 'pointer',
                  fontSize: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '4px'
                }}
                onMouseEnter={(e) => e.currentTarget.style.color = '#ffffff'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#64748b'}
              >
                <X size={14} />
              </button>
            </div>
          </div>

          {/* Body content based on active tab */}
          <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>

            {/* Tab 1: Console de Automação */}
            {bottomActiveTab === 'console' && (
              <div
                id="bottom-console-logs"
                style={{
                  flex: 1,
                  overflowY: 'auto',
                  padding: '12px',
                  background: '#040711',
                  fontFamily: 'monospace',
                  fontSize: '0.72rem',
                  color: '#34d399',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '4px'
                }}
              >
                {globalProcLogs.length === 0 ? (
                  <span style={{ color: '#475569', fontStyle: 'italic' }}>Nenhum processamento em lote ativo. Aguardando gatilho...</span>
                ) : (
                  globalProcLogs.map((log, idx) => {
                    let color = '#34d399';
                    if (log.includes('⚠️')) color = '#fbbf24';
                    if (log.includes('⛔') || log.includes('❌') || log.toLowerCase().includes('erro') || log.toLowerCase().includes('falha')) color = '#f87171';
                    if (log.includes('✅') || log.includes('SUCCESS')) color = '#6ee7b7';
                    return <div key={idx} style={{ color, whiteSpace: 'pre-wrap' }}>{log}</div>;
                  })
                )}
                <div id="bottom-console-anchor" />
              </div>
            )}

            {/* Tab 1b: Simulador Playwright */}
            {bottomActiveTab === 'simulator' && (
              <div
                id="bottom-simulator-logs"
                style={{
                  flex: 1,
                  overflowY: 'auto',
                  padding: '12px',
                  background: '#030712',
                  fontFamily: 'monospace',
                  fontSize: '0.72rem',
                  color: '#a78bfa',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '4px'
                }}
              >
                {simulationLogs.length === 0 ? (
                  <span style={{ color: '#475569', fontStyle: 'italic' }}>Nenhuma simulação ativa no momento.</span>
                ) : (
                  simulationLogs.map((log, idx) => {
                    let color = '#a78bfa';
                    if (log.includes('⚠️')) color = '#fbbf24';
                    if (log.includes('⛔') || log.includes('❌') || log.toLowerCase().includes('erro') || log.toLowerCase().includes('falha')) color = '#f87171';
                    if (log.includes('✅') || log.includes('SUCCESS') || log.includes('🎉')) color = '#34d399';
                    return <div key={idx} style={{ color, whiteSpace: 'pre-wrap' }}>{log}</div>;
                  })
                )}
                <div id="bottom-simulator-anchor" />
              </div>
            )}

            {/* Tab 2: Terminal Interativo (CLI) */}
            {bottomActiveTab === 'terminal' && (
              <div
                style={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  background: '#020617',
                  fontFamily: "'Consolas', 'monospace'",
                  fontSize: '0.75rem',
                  color: '#38bdf8',
                  padding: '12px'
                }}
                onClick={() => document.getElementById('terminal-cli-input')?.focus()}
              >
                {/* History */}
                <div
                  id="bottom-terminal-history"
                  style={{
                    flex: 1,
                    overflowY: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '4px',
                    marginBottom: '8px'
                  }}
                >
                  {terminalHistory.map((line, idx) => (
                    <div key={idx} style={{
                      whiteSpace: 'pre-wrap',
                      color: line.startsWith('ac-orchestrator') ? '#a78bfa' : line.startsWith('ac-cli:') ? '#ef4444' : '#38bdf8'
                    }}>
                      {line}
                    </div>
                  ))}
                  <div id="bottom-terminal-anchor" />
                </div>

                {/* Input Prompt */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', borderTop: '1px solid #1e293b', paddingTop: '6px' }}>
                  <span style={{ color: '#a78bfa', fontWeight: 700 }}>ac-orchestrator@aluno-conectado:~/automation$</span>
                  <input
                    id="terminal-cli-input"
                    type="text"
                    value={terminalInput}
                    onChange={(e) => setTerminalInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        executeTerminalCommand(terminalInput);
                      }
                    }}
                    placeholder='Digite um comando... (ex: "help", "status")'
                    style={{
                      flex: 1,
                      background: 'none',
                      border: 'none',
                      outline: 'none',
                      color: '#ffffff',
                      fontFamily: "'Consolas', 'monospace'",
                      fontSize: '0.75rem'
                    }}
                  />
                </div>
              </div>
            )}

            {/* Tab 3: Live Browser Preview */}
            {bottomActiveTab === 'preview' && (
              <div style={{
                flex: 1,
                background: '#0f172a',
                padding: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                {/* Simulated Chrome Browser */}
                <div style={{
                  width: '100%',
                  maxWidth: '780px',
                  height: '100%',
                  background: '#f8fafc',
                  border: '1px solid #334155',
                  borderRadius: '8px',
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
                }}>
                  {/* Faux Chrome Header */}
                  <div style={{
                    height: '28px',
                    background: '#e2e8f0',
                    borderBottom: '1px solid #cbd5e1',
                    display: 'flex',
                    alignItems: 'center',
                    padding: '0 8px',
                    gap: '8px',
                    justifyContent: 'space-between'
                  }}>
                    {/* Controls */}
                    <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                      <div style={{ width: '9px', height: '9px', borderRadius: '50%', background: '#ef4444' }} />
                      <div style={{ width: '9px', height: '9px', borderRadius: '50%', background: '#f59e0b' }} />
                      <div style={{ width: '9px', height: '9px', borderRadius: '50%', background: '#10b981' }} />
                    </div>
                    {/* Address Bar */}
                    <div style={{
                      flex: 1,
                      margin: '0 12px',
                      background: '#ffffff',
                      borderRadius: '4px',
                      height: '18px',
                      border: '1px solid #cbd5e1',
                      display: 'flex',
                      alignItems: 'center',
                      padding: '0 8px',
                      fontSize: '0.62rem',
                      color: '#475569',
                      fontFamily: 'monospace'
                    }}>
                      {isCurrentlyRunning
                        ? `http://localhost:3443${activeProgress < 40 ? '/login' : activeProgress < 60 ? '/conectando' : '/admin/dashboard'}`
                        : 'http://localhost:3443/'}
                    </div>
                    {/* Window Badge */}
                    <span style={{ fontSize: '0.58rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>
                      Chrome Sandbox
                    </span>
                  </div>

                  {/* Browser Screen Viewport */}
                  <div style={{
                    flex: 1,
                    background: '#ffffff',
                    color: '#0f172a',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '16px',
                    position: 'relative',
                    overflow: 'hidden'
                  }}>
                    {!isCurrentlyRunning ? (
                      <div style={{ textAlign: 'center', color: '#64748b' }}>
                        <div style={{ fontSize: '1.5rem', marginBottom: '8px' }}>🌐</div>
                        <span style={{ fontSize: '0.75rem', fontWeight: 600 }}>Simulador Inativo</span>
                        <p style={{ fontSize: '0.68rem', color: '#94a3b8', margin: '4px 0 0 0', maxWidth: '300px' }}>
                          Inicie uma simulação ou processamento em lote para visualizar o sandbox do navegador.
                        </p>
                      </div>
                    ) : (
                      // Dynamic view according to progress
                      (() => {
                        if (activeProgress < 25) {
                          // Login screen
                          return (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '220px', padding: '12px', border: '1px solid #e2e8f0', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.02)', animation: 'fadeIn 0.2s' }}>
                              <div style={{ fontSize: '0.78rem', fontWeight: 800, color: '#7c3aed', textAlign: 'center' }}>Aluno Conectado</div>
                              <input disabled type="text" value="admin@alunoconectado.com.br" style={{ fontSize: '0.68rem', padding: '4px 8px', border: '1px solid #cbd5e1', borderRadius: '4px' }} />
                              <input disabled type="password" value="••••••••••••" style={{ fontSize: '0.68rem', padding: '4px 8px', border: '1px solid #cbd5e1', borderRadius: '4px' }} />
                              <button disabled style={{ background: '#7c3aed', color: '#ffffff', border: 'none', borderRadius: '4px', padding: '6px', fontSize: '0.68rem', fontWeight: 700 }}>Entrar</button>
                              <div style={{ fontSize: '0.58rem', color: '#64748b', textAlign: 'center', fontStyle: 'italic' }}>
                                Preenchendo credenciais automaticamente...
                              </div>
                            </div>
                          );
                        }
                        if (activeProgress < 50) {
                          // Captcha Solver
                          return (
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', animation: 'fadeIn 0.2s' }}>
                              <div style={{ width: '40px', height: '40px', borderRadius: '50%', border: '3px solid #7c3aed', borderTopColor: 'transparent', animation: 'spin 1s linear infinite' }} />
                              <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#475569' }}>Resolvendo Desafio Captcha</span>
                              <span style={{ fontSize: '0.62rem', color: '#94a3b8' }}>Mapeando elementos de imagem via AI Alice...</span>
                            </div>
                          );
                        }
                        if (activeProgress < 75) {
                          // Verification / Redirecting
                          return (
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', animation: 'fadeIn 0.2s' }}>
                              <div style={{ color: '#10b981', fontSize: '1.5rem' }}>🔒</div>
                              <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#10b981' }}>Sessão Autorizada</span>
                              <span style={{ fontSize: '0.62rem', color: '#64748b' }}>Redirecionando para o painel principal...</span>
                            </div>
                          );
                        }
                        if (activeProgress < 95) {
                          // Form submission / Dashboard
                          return (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%', animation: 'fadeIn 0.2s' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #cbd5e1', paddingBottom: '4px' }}>
                                <span style={{ fontSize: '0.7rem', fontWeight: 800, color: '#1e293b' }}>Painel da Secretaria Escolar</span>
                                <span style={{ fontSize: '0.58rem', color: '#10b981', fontWeight: 700 }}>● Conectado</span>
                              </div>
                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                                <div style={{ background: '#f1f5f9', padding: '6px', borderRadius: '4px' }}>
                                  <span style={{ display: 'block', fontSize: '0.52rem', color: '#64748b' }}>Total Alunos</span>
                                  <span style={{ fontSize: '0.8rem', fontWeight: 800 }}>184 Alunos</span>
                                </div>
                                <div style={{ background: '#f1f5f9', padding: '6px', borderRadius: '4px' }}>
                                  <span style={{ display: 'block', fontSize: '0.52rem', color: '#64748b' }}>Matrículas Hoje</span>
                                  <span style={{ fontSize: '0.8rem', fontWeight: 800, color: '#7c3aed' }}>+12</span>
                                </div>
                              </div>
                              <div style={{ fontSize: '0.58rem', color: '#64748b', fontStyle: 'italic', background: '#faf5ff', padding: '4px 8px', borderRadius: '4px', border: '1px solid #f3e8ff' }}>
                                Executando: preenchendo formulário de matrícula de aluno síncrono.
                              </div>
                            </div>
                          );
                        }
                        // Completed Success
                        return (
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', animation: 'fadeIn 0.2s' }}>
                            <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: 'rgba(16,185,129,0.1)', color: '#10b981', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem' }}>✓</div>
                            <span style={{ fontSize: '0.75rem', fontWeight: 800, color: '#10b981' }}>Operação Concluída com Sucesso!</span>
                            <span style={{ fontSize: '0.62rem', color: '#64748b' }}>Simulação finalizada. Logs e PDFs exportados para auditoria.</span>
                          </div>
                        );
                      })()
                    )}
                  </div>
                </div>
              </div>
            )}

          </div>
        </div>
      </div>

      <style dangerouslySetInnerHTML={{
        __html: `
        .pulse-led-green {
          animation: pulse-green-anim 1.2s infinite alternate;
        }
        @keyframes pulse-green-anim {
          from { opacity: 0.4; transform: scale(0.9); }
          to { opacity: 1; transform: scale(1.15); box-shadow: 0 0 10px #10b981; }
        }
        #bottom-console-logs::-webkit-scrollbar,
        #bottom-terminal-history::-webkit-scrollbar {
          width: 6px;
        }
        #bottom-console-logs::-webkit-scrollbar-track,
        #bottom-terminal-history::-webkit-scrollbar-track {
          background: rgba(0, 0, 0, 0.2);
        }
        #bottom-console-logs::-webkit-scrollbar-thumb,
        #bottom-terminal-history::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.1);
          border-radius: 3px;
        }
        #bottom-console-logs::-webkit-scrollbar-thumb:hover,
        #bottom-terminal-history::-webkit-scrollbar-thumb:hover {
          background: rgba(255, 255, 255, 0.2);
        }
      `}} />

      {/* PDF PROGRESS TRACKING MODAL */}
      {pdfProgressShow && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: 'rgba(2, 6, 23, 0.75)',
          backdropFilter: 'blur(8px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
          animation: 'fadeIn 0.25s ease-out'
        }}>
          <div style={{
            background: '#0b0f19',
            border: '1px solid rgba(139, 92, 246, 0.2)',
            borderRadius: '16px',
            padding: '28px',
            width: '650px',
            boxShadow: '0 20px 50px rgba(0, 0, 0, 0.6), 0 0 30px rgba(139, 92, 246, 0.1)',
            display: 'flex',
            flexDirection: 'column',
            gap: '20px',
            position: 'relative'
          }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '10px',
                background: 'rgba(139, 92, 246, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#a78bfa'
              }}>
                <FileText size={20} />
              </div>
              <div>
                <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 800, color: '#f8fafc', fontFamily: "'Outfit', sans-serif" }}>
                  Geração de Manual PDF
                </h3>
                <span style={{ fontSize: '0.74rem', color: '#94a3b8' }}>
                  Compilando guia de treinamento passo a passo
                </span>
              </div>
            </div>

            {/* List of Steps */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginTop: '8px' }}>
              {[
                { stepNum: 1, label: 'Lendo tópicos e passos do roteiro' },
                { stepNum: 2, label: 'Processando capturas de tela e legendas' },
                { stepNum: 3, label: 'Estruturando layout e paginação do documento' },
                { stepNum: 4, label: 'Compilando PDF e salvando no servidor' }
              ].map((step) => {
                const isCompleted = pdfProgressStep > step.stepNum || pdfProgressStep === 5;
                const isActive = pdfProgressStep === step.stepNum && !pdfProgressError;
                const isPending = pdfProgressStep < step.stepNum && !pdfProgressError;

                return (
                  <div key={step.stepNum} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    opacity: isPending ? 0.4 : 1,
                    transition: 'opacity 0.25s'
                  }}>
                    {isCompleted ? (
                      <div style={{ color: '#10b981', display: 'flex', alignItems: 'center' }}>
                        <CheckCircle2 size={16} />
                      </div>
                    ) : isActive ? (
                      <div style={{ color: '#a78bfa', display: 'flex', alignItems: 'center' }}>
                        <Loader2 size={16} className="spin" />
                      </div>
                    ) : (
                      <div style={{
                        width: '16px',
                        height: '16px',
                        borderRadius: '50%',
                        border: '2px solid #475569',
                        boxSizing: 'border-box'
                      }} />
                    )}
                    <span style={{
                      fontSize: '0.8rem',
                      fontWeight: isActive ? 600 : 500,
                      color: isActive ? '#f8fafc' : isCompleted ? '#cbd5e1' : '#94a3b8'
                    }}>
                      {step.label}
                    </span>
                  </div>
                );
              })}
            </div>

            {/* Terminal de Logs do Processamento via WebSocket */}
            <div style={{
              background: '#040711',
              border: '1px solid rgba(255, 255, 255, 0.05)',
              borderRadius: '8px',
              padding: '12px',
              height: '180px',
              overflowY: 'auto',
              fontFamily: 'monospace',
              fontSize: '0.74rem',
              color: '#34d399',
              display: 'flex',
              flexDirection: 'column',
              gap: '6px',
              whiteSpace: 'pre-wrap',
            }}>
              {pdfLogs.length === 0 ? (
                <span style={{ color: '#64748b', fontStyle: 'italic' }}>Aguardando início do processamento...</span>
              ) : (
                pdfLogs.map((log, index) => {
                  let color = '#34d399'; // Info
                  if (log.includes('⚠️')) color = '#fbbf24'; // Warning
                  if (log.includes('❌') || log.toLowerCase().includes('erro') || log.toLowerCase().includes('fail')) color = '#f87171'; // Error
                  if (log.includes('🎉') || log.includes('✅')) color = '#6ee7b7'; // Ok / Success
                  return (
                    <div key={index} style={{ color }}>
                      {log}
                    </div>
                  );
                })
              )}
              <div ref={pdfLogsEndRef} />
            </div>

            {/* Status / Output */}
            {pdfProgressError ? (
              <div style={{
                background: 'rgba(239, 68, 68, 0.08)',
                border: '1px solid rgba(239, 68, 68, 0.2)',
                borderRadius: '10px',
                padding: '12px',
                fontSize: '0.78rem',
                color: '#f87171',
                display: 'flex',
                alignItems: 'start',
                gap: '8px',
                lineHeight: 1.4
              }}>
                <XCircle size={16} style={{ flexShrink: 0, marginTop: '2px' }} />
                <div>
                  <strong>Falha na geração do PDF:</strong>
                  <div style={{ marginTop: '4px', fontSize: '0.74rem', opacity: 0.9 }}>{pdfProgressError}</div>
                </div>
              </div>
            ) : pdfProgressStep === 5 ? (
              <div style={{
                background: 'rgba(16, 185, 129, 0.08)',
                border: '1px solid rgba(16, 185, 129, 0.2)',
                borderRadius: '10px',
                padding: '12px',
                fontSize: '0.78rem',
                color: '#34d399',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <CheckCircle2 size={16} style={{ flexShrink: 0 }} />
                <span>🎉 Manual PDF gerado e homologado com sucesso!</span>
              </div>
            ) : (
              <div style={{
                height: '4px',
                width: '100%',
                background: '#1e293b',
                borderRadius: '2px',
                overflow: 'hidden'
              }}>
                <div style={{
                  height: '100%',
                  width: `${(Math.min(pdfProgressStep, 4) / 4) * 100}%`,
                  background: 'linear-gradient(90deg, #8b5cf6 0%, #a78bfa 100%)',
                  borderRadius: '2px',
                  transition: 'width 0.4s ease-out'
                }} />
              </div>
            )}

            {/* Actions Footer */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '10px' }}>
              {pdfProgressStep === 5 && pdfProgressUrl && (
                <a
                  href={pdfProgressUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    padding: '8px 16px',
                    background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                    color: '#ffffff',
                    borderRadius: '8px',
                    fontSize: '0.8rem',
                    fontWeight: 700,
                    textDecoration: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    boxShadow: '0 4px 15px rgba(139, 92, 246, 0.25)'
                  }}
                >
                  Abrir Documento PDF
                  <ExternalLink size={12} />
                </a>
              )}

              <button
                onClick={() => setPdfProgressShow(false)}
                disabled={pdfProgressStep < 5 && !pdfProgressError}
                style={{
                  padding: '8px 16px',
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  color: pdfProgressStep < 5 && !pdfProgressError ? '#64748b' : '#cbd5e1',
                  borderRadius: '8px',
                  fontSize: '0.8rem',
                  fontWeight: 600,
                  cursor: pdfProgressStep < 5 && !pdfProgressError ? 'not-allowed' : 'pointer'
                }}
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL: ITENS ARQUIVADOS */}
      {showArchivedModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
          background: 'rgba(2, 6, 23, 0.75)', backdropFilter: 'blur(8px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
          animation: 'fadeIn 0.25s ease-out'
        }}>
          <div style={{
            background: '#0b0f19', border: '1px solid rgba(139, 92, 246, 0.2)',
            borderRadius: '16px', padding: '28px', width: '680px', display: 'flex', flexDirection: 'column', gap: '20px',
            boxShadow: '0 20px 50px rgba(0, 0, 0, 0.6), 0 0 30px rgba(139, 92, 246, 0.1)',
            maxHeight: '80vh', overflow: 'hidden'
          }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid rgba(255, 255, 255, 0.08)', paddingBottom: '14px' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 800, color: '#f8fafc', fontFamily: "'Outfit', sans-serif" }}>
                  Itens Arquivados
                </h3>
                <span style={{ fontSize: '0.74rem', color: '#94a3b8' }}>
                  Gerencie tópicos e roteiros que foram arquivados para este perfil
                </span>
              </div>
              <button
                onClick={() => setShowArchivedModal(false)}
                style={{
                  background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', padding: '4px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%'
                }}
                onMouseEnter={(e) => e.currentTarget.style.color = '#ffffff'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#94a3b8'}
              >
                <X size={18} />
              </button>
            </div>

            {/* Content Lists (2 Columns) */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', overflowY: 'auto', flex: 1, paddingRight: '4px' }}>

              {/* Col 1: Tópicos */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <h4 style={{ margin: 0, fontSize: '0.82rem', fontWeight: 800, color: '#c084fc', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Tópicos ({archivedTopics.length})
                </h4>
                {archivedTopics.length === 0 ? (
                  <div style={{ padding: '20px', border: '1px dashed rgba(255, 255, 255, 0.05)', borderRadius: '10px', textAlign: 'center', color: '#64748b', fontSize: '0.78rem' }}>
                    Nenhum tópico arquivado.
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {archivedTopics.map(topic => (
                      <div key={topic.id} style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px',
                        background: 'rgba(255, 255, 255, 0.02)', border: '1px solid rgba(255, 255, 255, 0.04)', borderRadius: '8px'
                      }}>
                        <span style={{ fontSize: '0.78rem', color: '#cbd5e1', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '180px' }} title={topic.titulo}>
                          {topic.titulo}
                        </span>
                        <button
                          onClick={() => handleRestoreEcadItem('TOPIC', topic.id)}
                          style={{
                            background: 'rgba(16, 185, 129, 0.1)', border: '1px solid rgba(16, 185, 129, 0.2)', borderRadius: '6px',
                            color: '#34d399', fontSize: '0.7rem', fontWeight: 700, padding: '4px 8px', cursor: 'pointer',
                            display: 'flex', alignItems: 'center', gap: '4px'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(16, 185, 129, 0.2)'}
                          onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(16, 185, 129, 0.1)'}
                        >
                          Recuperar
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Col 2: Subtópicos / Roteiros */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <h4 style={{ margin: 0, fontSize: '0.82rem', fontWeight: 800, color: '#c084fc', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Roteiros ({archivedSubtopics.length})
                </h4>
                {archivedSubtopics.length === 0 ? (
                  <div style={{ padding: '20px', border: '1px dashed rgba(255, 255, 255, 0.05)', borderRadius: '10px', textAlign: 'center', color: '#64748b', fontSize: '0.78rem' }}>
                    Nenhum roteiro arquivado.
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {archivedSubtopics.map(({ topico, sub }) => (
                      <div key={sub.id} style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px',
                        background: 'rgba(255, 255, 255, 0.02)', border: '1px solid rgba(255, 255, 255, 0.04)', borderRadius: '8px'
                      }}>
                        <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1, paddingRight: '8px' }}>
                          <span style={{ fontSize: '0.6rem', color: '#64748b', textTransform: 'uppercase', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={topico.titulo}>
                            {topico.titulo}
                          </span>
                          <span style={{ fontSize: '0.78rem', color: '#cbd5e1', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={sub.titulo}>
                            {sub.titulo}
                          </span>
                        </div>
                        <button
                          onClick={() => handleRestoreEcadItem('SUBTOPIC', sub.id)}
                          style={{
                            background: 'rgba(16, 185, 129, 0.1)', border: '1px solid rgba(16, 185, 129, 0.2)', borderRadius: '6px',
                            color: '#34d399', fontSize: '0.7rem', fontWeight: 700, padding: '4px 8px', cursor: 'pointer',
                            display: 'flex', alignItems: 'center', gap: '4px'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(16, 185, 129, 0.2)'}
                          onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(16, 185, 129, 0.1)'}
                        >
                          Recuperar
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

            </div>

            {/* Footer */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', borderTop: '1px solid rgba(255, 255, 255, 0.08)', paddingTop: '14px' }}>
              <button
                onClick={() => setShowArchivedModal(false)}
                style={{
                  padding: '8px 16px', background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)',
                  color: '#cbd5e1', borderRadius: '8px', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.08)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* COMPARISON ZOOM OVERLAY MODAL */}
      <EcadZoomModal
        zoomImage={zoomImage}
        setZoomImage={setZoomImage}
        selectedSubtopicoId={selectedSubtopicoId}
        passos={passos}
      />

      {/* CRUD CREATIONS & PAGE SCANNER POPUP MODALS */}
      <EcadModals
        perfis={mappedPerfis}
        showPerfilModal={showPerfilModal}
        setShowPerfilModal={setShowPerfilModal}
        perfilForm={perfilForm}
        setPerfilForm={setPerfilForm}
        handleCreatePerfil={handleCreatePerfil}

        showTopicoModal={showTopicoModal}
        setShowTopicoModal={setShowTopicoModal}
        topicoForm={topicoForm}
        setTopicoForm={setTopicoForm}
        handleCreateTopico={handleCreateTopico}

        showSubtopicoModal={showSubtopicoModal}
        setShowSubtopicoModal={setShowSubtopicoModal}
        subtopicoForm={subtopicoForm}
        setSubtopicoForm={setSubtopicoForm}
        handleCreateSubtopico={handleCreateSubtopico}

        showScannerModal={showScannerModal}
        setShowScannerModal={setShowScannerModal}
        scannerForm={scannerForm}
        setScannerForm={setScannerForm}
        scanningPage={scanningPage}
        handleScanPage={handleScanPage}
      />

      {/* CSS Spin Animation & Timeline Scrollbar */}
      <style dangerouslySetInnerHTML={{
        __html: `
        .spin {
          animation: spin-anim 1s linear infinite;
        }
        @keyframes spin-anim {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        .timeline-container::-webkit-scrollbar {
          width: 6px;
        }
        .timeline-container::-webkit-scrollbar-track {
          background: rgba(0, 0, 0, 0.1);
        }
        .timeline-container::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.15);
          border-radius: 3px;
        }
        .timeline-container::-webkit-scrollbar-thumb:hover {
          background: rgba(255, 255, 255, 0.3);
        }
      `}} />
    </div>
  );
}
